/* AtomiQ scroll runtime (v4). A section marked as an act publishes how far the visitor has scrolled through it
   as --p (0 to 1) on the act element; cues inside it show between two progress values, and clip reveals wipe
   media open with the scroll. Pinned acts keep a sticky stage on screen while the page moves under it.
   The v3 films, kinetic type and pointer effects are gone; the site-wide motion switch lives in theme.js.
   Markup:
     <section data-aq-act="pin|flow" style="--span: 1.6">       pin: the stage sticks for (span - 1) screens
       <div data-aq-stage> ... </div>                             the sticky stage (pin only)
       <p data-aq-cue="0.2 0.6">                                  visible between 0.2 and 0.6 (one value: holds)
       <figure data-aq-reveal="up|down|left|right|iris" data-aq-at="0.1 0.5">
   With reduced motion, Save-Data or no JS everything is visible and static. */
(() => {
  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lean = navigator.connection?.saveData === true;
  const still = reduce || window.AQ?.motion === false;
  const coarse = matchMedia('(hover: none) and (pointer: coarse)').matches;
  const smallQuery = matchMedia('(max-width: 899px)');

  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const smooth = (x) => x * x * (3 - 2 * x);
  const numbers = (value) => (value || '').trim().split(/\s+/).filter(Boolean).map(Number);

  let acts = [];
  let lastY = -1;
  let dirty = true;
  let viewport = innerHeight;
  let lastWidth = innerWidth;
  let running = false;
  let idle = 0;

  /* ---------------------------------------------------------------- the 3D explainer under reduced motion
     Besluit 5: with reduced motion, Save-Data or 3D switched off in the theme settings, the explainer shows the
     poster of each stage and the controls switch posters. Without data-three it never starts WebGL. */
  function holdScenes() {
    if (!(still || lean)) return;
    document.querySelectorAll('aq-explainer[data-three], .aq-3d[data-three]').forEach((scene) => scene.removeAttribute('data-three'));
  }
  holdScenes();

  /* ---------------------------------------------------------------- collect and measure */

  function parseCue(el) {
    /* A phone can carry its own window, for stages that stack differently. */
    const [from = 0, to, rampIn, rampOut] = numbers((smallQuery.matches && el.dataset.aqCueM) || el.dataset.aqCue);
    const hold = to === undefined;
    const end = hold ? 1 : to;
    const span = Math.max(end - from, 0.0001);
    return {
      el,
      from,
      to: end,
      hold,
      rampIn: (rampIn === undefined ? (hold ? 0.5 : 0.3) : rampIn) * span,
      rampOut: hold ? 0 : (rampOut === undefined ? 0.3 : rampOut) * span,
      opacity: -1,
      shown: null,
    };
  }

  function collect() {
    acts = [...document.querySelectorAll('[data-aq-act]')].map((el) => {
      const type = el.dataset.aqAct || 'flow';
      return {
        el,
        type,
        pinned: type !== 'flow',
        stage: type !== 'flow' ? el.querySelector('[data-aq-stage]') : null,
        cues: [...el.querySelectorAll('[data-aq-cue]')].map(parseCue),
        reveals: [...el.querySelectorAll('[data-aq-reveal]')].map((node) => {
          const [from = 0, to = 0.5] = numbers(node.dataset.aqAt);
          return { el: node, mode: node.dataset.aqReveal || 'up', from, to };
        }),
        p: -1,
        near: null,
      };
    });
    measure();
  }

  function measure() {
    viewport = innerHeight;
    const y = scrollY;
    for (const act of acts) {
      const rect = act.el.getBoundingClientRect();
      act.top = rect.top + y;
      act.height = rect.height;
      act.stageH = act.stage ? act.stage.offsetHeight : viewport;
      act.offset = act.stage ? parseFloat(getComputedStyle(act.stage).top) || 0 : 0;
    }
    dirty = true;
    run();
  }

  /* ---------------------------------------------------------------- per frame */

  function cueOpacity(cue, p) {
    if (p < cue.from) return 0;
    const fadeIn = cue.rampIn > 0 ? clamp((p - cue.from) / cue.rampIn) : 1;
    if (cue.hold) return smooth(fadeIn);
    if (p > cue.to) return 0;
    const fadeOut = cue.rampOut > 0 ? clamp((cue.to - p) / cue.rampOut) : 1;
    return smooth(Math.min(fadeIn, fadeOut));
  }

  function paintCue(cue, p) {
    const opacity = still ? (p >= cue.from && (cue.hold || p <= cue.to) ? 1 : 0) : cueOpacity(cue, p);
    if (Math.abs(opacity - cue.opacity) < 0.004) return;
    cue.opacity = opacity;
    cue.el.style.opacity = opacity.toFixed(3);
    const shown = opacity > 0.01;
    if (shown !== cue.shown) {
      cue.shown = shown;
      cue.el.toggleAttribute('data-aq-shown', shown);
    }
    if (!still && cue.el.dataset.aqRise !== '0') {
      const entering = cue.rampIn > 0 && p < cue.from + cue.rampIn;
      const offset = entering ? (1 - opacity) * 18 : (1 - opacity) * -12;
      cue.el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    }
  }

  function paintReveal(reveal, p) {
    const t = still ? 1 : smooth(clamp((p - reveal.from) / Math.max(reveal.to - reveal.from, 0.0001)));
    const rest = ((1 - t) * 100).toFixed(2);
    const shapes = {
      up: `inset(${rest}% 0 0 0)`,
      down: `inset(0 0 ${rest}% 0)`,
      left: `inset(0 ${rest}% 0 0)`,
      right: `inset(0 0 0 ${rest}%)`,
      iris: `circle(${(t * 75).toFixed(2)}% at 50% 50%)`,
    };
    reveal.el.style.clipPath = shapes[reveal.mode] || shapes.up;
  }

  function update(act, y) {
    const near = y > act.top - viewport * 1.5 && y < act.top + act.height + viewport * 0.5;
    if (!near && act.near === false) return;
    act.near = near;
    /* A pinned stage travels (height - stage) while it sticks; a stage of one screen simply stands still. */
    const travel = act.height - act.stageH;
    const raw = act.pinned
      ? (travel > 8 ? (y + act.offset - act.top) / travel : 0)
      : (y + viewport - act.top) / (act.height + viewport);
    const p = clamp(raw);
    if (Math.abs(p - act.p) < 0.0002) return;
    act.p = p;
    act.el.style.setProperty('--p', p.toFixed(4));
    for (const cue of act.cues) paintCue(cue, p);
    for (const reveal of act.reveals) paintReveal(reveal, p);
  }

  /* The loop runs while the page moves and stops half a second after it comes to rest; a scroll, a resize or a new
     measurement starts it again. So a page at rest does no work here at all. */
  function frame() {
    const y = scrollY;
    if (dirty || y !== lastY) {
      for (const act of acts) update(act, y);
      lastY = y;
      dirty = false;
      idle = 0;
    } else {
      idle += 1;
    }
    if (acts.length && idle < 30) requestAnimationFrame(frame);
    else running = false;
  }

  function run() {
    if (running || !acts.length) return;
    running = true;
    idle = 0;
    requestAnimationFrame(frame);
  }

  function refresh() {
    for (const act of acts) {
      act.p = -1;
      act.near = null;
      for (const cue of act.cues) cue.opacity = -1;
    }
    holdScenes();
    collect();
    run();
  }

  function start() {
    collect();
    root.classList.add('aq-scroll');
    run();
  }

  /* ---------------------------------------------------------------- wiring */

  addEventListener(
    'resize',
    () => {
      /* Phones resize on every show and hide of the address bar; only a change in width is a new layout. */
      if (innerWidth === lastWidth && coarse) {
        viewport = innerHeight;
        return;
      }
      lastWidth = innerWidth;
      measure();
    },
    { passive: true }
  );
  addEventListener('scroll', run, { passive: true });
  addEventListener('load', measure);
  document.fonts?.ready.then(measure);
  /* The page height changes as pictures and sections settle: measure again, in the next frame and only when the
     height really moved. Measuring inside the observer's own callback made WebKit report "ResizeObserver loop
     completed with undelivered notifications" as a console error on short pages. */
  if ('ResizeObserver' in window) {
    let height = 0;
    let pending = 0;
    const check = () => {
      pending = 0;
      const next = root.scrollHeight;
      if (Math.abs(next - height) > 4) {
        height = next;
        measure();
      }
    };
    new ResizeObserver(() => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(check);
    }).observe(document.body);
  }
  smallQuery.addEventListener?.('change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));

  /* A control inside a cue that is still dark would take focus unseen: bring its act to the cue's plateau. */
  document.addEventListener('focusin', (event) => {
    const cueEl = event.target.closest?.('[data-aq-cue]');
    if (!cueEl || Number(cueEl.style.opacity || 1) > 0.85) return;
    const act = acts.find((entry) => entry.el.contains(cueEl));
    const cue = act?.cues.find((entry) => entry.el === cueEl);
    if (!act?.pinned || !cue) return;
    const middle = cue.hold ? Math.min(cue.from + cue.rampIn + 0.02, 1) : (cue.from + cue.to) / 2;
    scrollTo({ top: act.top - act.offset + middle * (act.height - act.stageH), behavior: 'instant' });
  });

  /* The motion switch moved to theme.js (AQ.motionSwitch); these stay for code written against v3. */
  window.AQScroll = {
    refresh,
    measure,
    setPaused: (on) => window.AQ?.motionSwitch?.set(on),
    get paused() {
      return Boolean(window.AQ?.motionSwitch?.paused);
    },
    get acts() {
      return acts;
    },
    get frames() {
      return [];
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
